import { useState, useEffect, useMemo } from 'react';
import { ShieldCheck, Search, RefreshCw, Plus, Trash2, X } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { postRequest } from "../utils/networking";
import { apiUrls } from "../utils/url";
import Pagination from '../components/Pagination';

export default function CourseAccessPage() {
  const [accessList, setAccessList] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accessRes, usersRes, coursesRes] = await Promise.all([
        postRequest(apiUrls.getcourseaccess, {}),
        postRequest(apiUrls.getallcousers, {}),
        postRequest(apiUrls.getcourse, { flag: "S" })
      ]);

      if (accessRes?.status === 'S') setAccessList(accessRes.data || []);
      if (usersRes?.status === 'S') setUsers(usersRes.data || []);
      if (coursesRes?.status === 'S' || coursesRes?.status === 's') setCourses(coursesRes.data || []);
    } catch (err) {
      toast.error("Network error while fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedCourse) {
      toast.warning("Please select both a user and a course");
      return;
    }
    
    setSaving(true);
    try {
      const res = await postRequest(apiUrls.createcourseaccess, {
        userid: selectedUser,
        courseid: selectedCourse
      });
      
      if (res?.status === 'S' || res?.status === 's') {
        toast.success(res.message || "Course assigned successfully");
        setShowModal(false);
        setSelectedUser('');
        setSelectedCourse('');
        fetchData();
      } else {
        toast.error(res?.message || "Failed to assign course");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accessid) => {
    if (!window.confirm("Are you sure you want to remove this course access?")) return;
    
    try {
      const res = await postRequest(apiUrls.deletecourseaccess, { accessid });
      if (res?.status === 'S' || res?.status === 's') {
        toast.success("Access removed");
        fetchData();
      } else {
        toast.error(res?.message || "Failed to remove access");
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  const filteredList = useMemo(() => {
    return accessList.filter(item => 
      (item.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.coursename || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [accessList, searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);

  return (
    <div className="h-full flex flex-col bg-slate-50 p-6 overflow-hidden relative">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input 
            type="text"
            placeholder="Search user or course..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 w-64 bg-white"
          />
        </div>
        <div className="flex items-center gap-3">

          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Assign Course
          </button>
        </div>
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1 custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-sm shadow-sm z-10">
              <tr className="border-b border-slate-200 text-slate-600 text-sm">
                <th className="font-semibold py-3 px-6 w-16">S.No</th>
                <th className="font-semibold py-3 px-6">User</th>
                <th className="font-semibold py-3 px-6">Course</th>
                <th className="font-semibold py-3 px-6">Assigned On</th>
                <th className="font-semibold py-3 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
                      <p>Loading access list...</p>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-slate-500">
                    No access records found.
                  </td>
                </tr>
              ) : (
                currentItems.map((item, idx) => (
                  <tr key={item.accessid} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-6 text-sm text-slate-500">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="py-3 px-6">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-800">{item.username}</span>
                        <span className="text-xs text-slate-500">{item.email}</span>
                      </div>
                    </td>
                    <td className="py-3 px-6">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {item.coursename}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-sm text-slate-500">{item.createdon}</td>
                    <td className="py-3 px-6 text-center">
                      <button 
                        onClick={() => handleDelete(item.accessid)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove Access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="border-t border-slate-200 p-4 shrink-0 bg-white">
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-orange-500" /> Assign Course
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAssign} className="p-5 flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select User</label>
                <Select
                  options={users.map(u => ({ value: u.userid, label: `${u.username} (${u.email})` }))}
                  value={selectedUser ? { value: selectedUser, label: users.find(u => u.userid === selectedUser)?.username + ' (' + users.find(u => u.userid === selectedUser)?.email + ')' } : null}
                  onChange={(option) => setSelectedUser(option ? option.value : '')}
                  placeholder="-- Choose User --"
                  isSearchable
                  isClearable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#f97316' : '#cbd5e1',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(249, 115, 22, 0.2)' : 'none',
                      borderRadius: '0.5rem',
                      padding: '0.125rem 0',
                      fontSize: '0.875rem',
                      '&:hover': {
                        borderColor: state.isFocused ? '#f97316' : '#cbd5e1'
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: '0.875rem',
                      backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fff7ed' : 'white',
                      color: state.isSelected ? 'white' : '#334155',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#f97316' : '#fff7ed',
                      }
                    })
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Course</label>
                <Select
                  options={courses.map(c => ({ value: c.courseid, label: c.coursename }))}
                  value={selectedCourse ? { value: selectedCourse, label: courses.find(c => c.courseid === selectedCourse)?.coursename } : null}
                  onChange={(option) => setSelectedCourse(option ? option.value : '')}
                  placeholder="-- Choose Course --"
                  isSearchable
                  isClearable
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#f97316' : '#cbd5e1',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(249, 115, 22, 0.2)' : 'none',
                      borderRadius: '0.5rem',
                      padding: '0.125rem 0',
                      fontSize: '0.875rem',
                      '&:hover': {
                        borderColor: state.isFocused ? '#f97316' : '#cbd5e1'
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: '0.875rem',
                      backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fff7ed' : 'white',
                      color: state.isSelected ? 'white' : '#334155',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#f97316' : '#fff7ed',
                      }
                    })
                  }}
                />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? "Assigning..." : "Assign Access"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
